<template>
    <div class="profile-container">
        <div class="md-layout">
            <div class="md-layout-item md-medium-size-25 md-xsmall-size-100 md-size-25">
                <md-card class="md-card-profile">
                    <div
                        class="md-card-avatar upload-container"
                        @mouseover="showUploadButton = true"
                        @mouseleave="showUploadButton = false"
                    >
                        <img
                            v-if="!photoPreview"
                            class="profile-image"
                            :src="member.photoUrl !== null ? member.photoUrl : $root.defaultPhotoUrl"
                            @error="$_setDefaultBrokenImage"
                            alt
                        />
                        <img
                            v-if="photoPreview"
                            class="profile-image"
                            :src="photoPreview"
                            @error="$_setDefaultBrokenImage"
                            alt
                        />
                        <div v-if="showUploadButton && canUpdateMember">
                            <input
                                ref="photoInput"
                                accept="image/png, image/gif, image/jpeg, image/jpg, image/gif, image/bmp, image/webp"
                                hidden
                                type="file"
                                @change="onPhotoSelected"
                            />
                            <div>
                                <button class="custom-btn" @click="$refs.photoInput.click()">Update Photo</button>
                            </div>
                        </div>
                    </div>

                    <md-card-content class="card-container">
                        <h6 class="category text-gray">
                            {{ displayRole }}
                        </h6>
                        <span class="card-description">
                            Date Joined: {{ member.dateJoined | dateFormat(DATE_TYPES.standardDate) }}
                        </span>
                        <h4 class="card-title">
                            {{ displayName }}
                        </h4>

                        <md-button
                            v-if="canRequestDeviceLog(member.deviceType)"
                            class="md-primary text-center"
                            @click="handleRequestDeviceLog(member.publicUserId)"
                        >
                            Request Device Logs
                        </md-button>
                        <md-button
                            v-if="
                                member.disabledDate === null &&
                                    user.publicUserId !== member.publicUserId &&
                                    canUpdateMember
                            "
                            class="md-default text-center"
                            @click="updateTeamMemberStatus(member.publicUserId, 'disable')"
                        >
                            Disable Member
                        </md-button>
                        <md-button
                            v-if="
                                member.disabledDate != null &&
                                    user.publicUserId != member.publicUserId &&
                                    canUpdateMember
                            "
                            class="md-default text-center md-success"
                            @click="updateTeamMemberStatus(member.publicUserId, 'enable')"
                        >
                            Enable Member
                        </md-button>
                        <md-button
                            v-if="
                                member.disabledDate == null &&
                                    member.isLockedOut &&
                                    user.publicUserId != member.publicUserId &&
                                    canUpdateMember
                            "
                            class="md-default text-center md-warning"
                            @click="updateTeamMemberStatus(member.publicUserId, 'unlock')"
                        >
                            Unlock Member
                        </md-button>
                    </md-card-content>
                </md-card>
            </div>
            <div class="md-layout-item md-medium-size-75 md-xsmall-size-100 md-size-75">
                <md-card>
                    <md-card-content class="custom-card-content">
                        <tabs
                            ref="memberTab"
                            :tab-name="tabs"
                            message-text="There are pending changes. Are you sure you want to switch tabs?"
                            @prompt-response="handleTabSwitch"
                            :selected-panel="activeTab"
                            :show-prompt="true"
                            color-button="primary"
                            class="custom-tab-list"
                        >
                            <template slot="tab-pane-1">
                                <member-details
                                    ref="details"
                                    :data="member"
                                    :is-allowed-to-update="canUpdateMember"
                                    @updated="handleUpdate"
                                />
                            </template>
                            <template slot="tab-pane-2">
                                <member-document
                                    ref="documents"
                                    :data="member"
                                    :is-allowed-to-update="canUpdateMember"
                                    @updated="handleUpdate"
                                />
                            </template>
                            <template slot="tab-pane-3">
                                <member-accounting-details
                                    :data="member"
                                    ref="accounts"
                                    :is-allowed-to-update="canEditAccountsTab"
                                    @updated="handleUpdate"
                                />
                            </template>
                            <template slot="tab-pane-4">
                                <member-roster-configuration
                                    ref="rosterConfiguration"
                                    :public-user-id="$route.params.publicUserId"
                                    :roster-configuration-data="rosterConfigurationData"
                                    @rosterConfigurationUpdated="handleRosterConfigurationUpdated"
                                />
                            </template>
                        </tabs>
                    </md-card-content>
                </md-card>

                <md-card class="history-section" v-if="canViewHistory">
                    <md-card-content>
                        <history-component
                            ref="historyComponent"
                            :view-type="CHANGE_HISTORY_VIEW_TYPES.TEAM_MEMBER"
                            :entity-id="member.userId || 0"
                            :page-size="15"
                            title="Profile Change History"
                        />
                    </md-card-content>
                </md-card>
            </div>
        </div>
    </div>
</template>

<script>
import { handleRequests, showErrorMessage, isValidImageFileType, isValidFileName } from '@/helpers';
import { GeneralMixin } from '@/mixins/GeneralMixin';
import { FormattingMixin } from '@/mixins/FormattingMixin';
import { mapGetters } from 'vuex';
import {
    APPLY_RATES_TO,
    INVOICE_LINE_TYPES,
    DATE_TYPES,
    FEATURE_NAMES,
    CHANGE_HISTORY_VIEW_TYPES
} from '@/utils/constants';
import { PAYMENT_METHOD_DETAILS_DEFAULT, TEAM_MEMBER_ROSTER_CONFIGURATION_DEFAULTS } from '@/utils/defaults';
import { BlockBlobClient } from '@azure/storage-blob';
import { Tabs, HistoryComponent } from '@/components';

import { FeatureManager } from '@/directives';
import _ from 'lodash';

import moment from 'moment';
import { MemberDetails, MemberDocument, MemberAccountingDetails, MemberRosterConfiguration } from './components';

export default {
    name: 'TeamMemberDetails',
    mixins: [GeneralMixin, FormattingMixin, FeatureManager],
    components: {
        Tabs,
        MemberDetails,
        MemberDocument,
        MemberAccountingDetails,
        MemberRosterConfiguration,
        HistoryComponent
    },
    data() {
        return {
            FEATURE_NAMES,
            DATE_TYPES,
            CHANGE_HISTORY_VIEW_TYPES,
            member: {
                paymentDetails: PAYMENT_METHOD_DETAILS_DEFAULT(),
                skills: [],
                serviceAreas: [],
                teamRegionId: null,
                vehicleCapacity: {},
                driversLicenseNumber: null,
                driversLicenseExpiryDate: null,
                insurancePolicyExpiryDate: null,
                insurancePolicyNumber: null,
                tagsUpdated: false,
                tags: [],
                tagsAdded: [],
                tagsDeleted: [],
                appPermissions: {
                    qrFrequentAddress: false,
                    views: {
                        items: false,
                        itemChecklist: false,
                        itemChecklistScanning: false,
                        default: ''
                    }
                }
            },
            selectedPhoto: null,
            photoPreview: null,
            showUploadButton: false,
            displayName: '',
            displayRole: '',
            oldEmail: null,
            rateGroups: [],
            rateGroupId: -1,
            oldAccountNumber: null,
            businessRegistrationNumberLabel: 'Business Registration Number',
            applyRatesTo: APPLY_RATES_TO,
            applyRatesToValue: [1, 2, 4],
            invoiceLineOptions: INVOICE_LINE_TYPES,
            hasPendingChanges: false,
            activeTab: 'Details',
            rosterConfigurationData: Object.assign({}, TEAM_MEMBER_ROSTER_CONFIGURATION_DEFAULTS),
            businessEntities: [],
            allTags: []
        };
    },
    computed: {
        ...mapGetters({
            user: 'user/user',
            hasTeam: 'user/hasTeam',
            hasAssetAccess: 'user/hasAssetAccess',
            isSupportUser: 'user/isSupportUser',
            isSupportAdminUser: 'user/isSupportAdminUser',
            canEditOwnTeamRegion: 'user/canEditOwnTeamRegion',
            teamRegions: 'team/teamRegions'
        }),
        canUpdateMember() {
            // can be moved to somewhere else
            const roleWeight = {
                'Team Owner': 13,
                'Office Staff': 8,
                'Team Manager': 5,
                'Team Member': 3
            };

            return roleWeight[this.user.role] >= (roleWeight[this.member.role] || 0);
        },
        canEditAccountsTab() {
            return this.user.role === 'Team Owner';
        },
        canViewHistory() {
            return this.user.role === 'Team Owner' || this.user.role === 'Team Manager';
        },
        tabs() {
            const headerTabs = ['Details', 'Documents', 'Accounts'];

            if (this.$_hasFeature(FEATURE_NAMES.TeamMemberRosterConfiguration)) {
                headerTabs.push('Rostering');
            }

            return headerTabs;
        }
    },
    async mounted() {
        this.$_handleLoaderState(true);
        const api = `/api/team-members/${this.$route.params.publicUserId}/`;
        await handleRequests(api).then(async (response) => {
            const details = { ...response.data.data };
            if (!details.teamRegionId) 
                details.teamRegionId = -1; // Not Set

            this.member = _.cloneDeep(details);
            if (this.member.userId) {
                this.member.userId = Number(this.member.userId);
            }
            this.displayName = this.member.fullName;
            this.displayRole = this.member.role;

            this.member.paymentDetails = _.cloneDeep(details.paymentDetails || PAYMENT_METHOD_DETAILS_DEFAULT());

            if (this.member.licenseExpiryDate != null) {
                this.member.licenseExpiryDate = moment(this.member.licenseExpiryDate).format(
                    DATE_TYPES.internationalDate
                );
            }

            if (this.member.policyExpiryDate != null) {
                this.member.policyExpiryDate = moment(this.member.policyExpiryDate).format(
                    DATE_TYPES.internationalDate
                );
            }

            // Load Rate Groups and Business Entities for history lookups
            await Promise.all([this.loadRateGroups(), this.loadBusinessEntities(), this.loadAllTags()]);

            if (this.$_hasFeature(FEATURE_NAMES.TeamMemberRosterConfiguration)) {
                await this.getRosterConfiguration();
            } else {
                this.$_handleLoaderState(false);
            }
        });
    },
    methods: {
        hasValidFileName(fileName) {
            if (!isValidFileName(fileName)) {
                this.$notify({
                    message: `Invalid file name. File name can't contain any of the following characters: \\/:*?"<>|. File name length should not exceed 100 characters'`,
                    type: 'danger'
                });
                return false;
            }
            return true;
        },
        isAValidImageType(type) {
            if (!isValidImageFileType(type)) {
                this.$notify({
                    message:
                        'Invalid file type. Please upload an image with file type (jpeg, jpg, png, gif, bmp, webp)',
                    type: 'danger'
                });
                return false;
            }
            return true;
        },
        async handleUpdate(data) {
            this.$_handleLoaderState(true, 'UPDATING...');

            if (data.licenseExpiryDate != null) {
                data.licenseExpiryDate = moment(data.licenseExpiryDate).format(DATE_TYPES.internationalDate);
            }

            if (data.policyExpiryDate != null) {
                data.policyExpiryDate = moment(data.policyExpiryDate).format(DATE_TYPES.internationalDate);
            }

            // Convert empty string to null for emissionPerDistance
            if (data.emissionPerDistance === '' || data.emissionPerDistance === undefined) {
                data.emissionPerDistance = null;
            }

            const api = `/api/team-members/${this.$route.params.publicUserId}`;
            const payload = {
                method: 'put',
                data: {
                    Role: [data.role],
                    Member: data
                }
            };

            await handleRequests(api, payload).then(
                async (response) => {
                    this.$_handleLoaderState(false);
                    Object.assign(this.member, data);
                    if (this.displayRole !== data.role) {
                        this.displayRole = data.role;

                        this.$messageBox.show({
                            body:
                                'Please note that the team member will need to log out and back in to have their role change take effect',
                            buttons: ['Ok']
                        });
                    } else {
                        this.$notify({
                            message: 'Member profile successfully updated',
                            type: 'success'
                        });
                    }

                    this.displayName = `${data.firstName} ${data.lastName}`;
                    this.accountNumberMasking();

                    if (data.tagsUpdated) 
                        await this.getTeamMemberTags();

                    if (data.addedOnHoldDays) 
                        await this.getTeamMemberOnHoldDays();

                    this.member.tagsAdded = [];
                    this.member.tagsDeleted = [];

                    // Refresh history component after saving Accounts or Details tab changes
                    this.refreshHistory();
                },
                (error) => {
                    this.$_handleLoaderState(false);
                    // Handle member code duplicate error specifically
                    if (error && error.data && Array.isArray(error.data)) {
                        const duplicateMemberCodeError = error.data.find(
                            (e) => e.errorCode === 'DUPLICATE_MEMBER_CODE'
                        );
                        if (duplicateMemberCodeError) {
                            this.$notify({
                                message: 'Member Code already exists within the team. Please choose a different code.',
                                type: 'danger'
                            });
                            return;
                        }
                    }

                    // Handle other errors using the existing showErrorMessage helper
                    const message = 'Error in updating member profile';
                    showErrorMessage(this, message, error);
                }
            );
        },
        updateTeamMemberStatus(publicUserId, type) {
            this.$_handleLoaderState(true, 'UPDATING...');
            const payload = {
                method: 'post'
            };
            const api = `/api/team-members/${publicUserId}/${type}`;
            handleRequests(api, payload).then(
                (response) => {
                    this.$_handleLoaderState(false);
                    const msg = type === 'unlock' ? 'Member unlocked!' : `Member ${type}d!`;
                    this.$notify({
                        message: msg,
                        type: 'success'
                    });
                    if (type === 'enable') {
                        this.member.disabledDate = null;
                    } else if (type === 'unlock') {
                        this.member.isLockedOut = false;
                    } else {
                        this.member.disabledDate = new Date().getUTCDate;
                    }
                },
                (error) => {
                    this.$_handleLoaderState(false);
                    const message = 'Error in enabling/disabling member';
                    showErrorMessage(this, message, error);
                }
            );
        },
        onPhotoSelected(event) {
            const [file] = event.target.files;

            if (!this.isAValidImageType(file.type) || !this.hasValidFileName(file.name)) {
                return;
            }

            this.selectedPhoto = file;
            this.photoPreview = URL.createObjectURL(this.selectedPhoto);
            this.onUploadImage();
        },
        async onUploadImage() {
            this.$_handleLoaderState(true, 'UPLOADING...');
            const file = this.selectedPhoto;
            const fileType = file.type.split('/');

            if (!this.isAValidImageType(file.type) || !this.hasValidFileName(file.name)) {
                this.$_handleLoaderState(false);
                return;
            }

            let ext = fileType[1];

            if (ext === 'jpeg') {
                ext = 'jpg';
            }

            const api = `/api/team-members/${this.$route.params.publicUserId}/photo/?fileType=${ext}`;
            const response = await handleRequests(api, { method: 'post' });

            const blockBlobClient = new BlockBlobClient(response.data.uri);

            try {
                await blockBlobClient.upload(file, file.size);

                this.$notify({
                    message: `Team member's photo has been successfully uploaded. Please wait a moment for it to be processed - it will reflect on your team member profile shortly.`,
                    type: 'success'
                });

                this.selectedPhoto = null;
                this.$_handleLoaderState(false);
            } catch (e) {
                const message = 'Error in uploading photo';
                showErrorMessage(this, message, e);
                this.$_handleLoaderState(false);
            }
        },
        accountNumberMasking() {
            if (
                this.member.paymentDetails.bank.accountNumber &&
                !this.member.paymentDetails.bank.accountNumber.includes('*') &&
                this.member.paymentDetails.bank.accountNumber.length > 2
            ) {
                const { accountNumber } = this.member.paymentDetails.bank;
                this.member.paymentDetails.bank.accountNumber = this.$_getMaskingText(accountNumber);
                this.oldAccountNumber = this.member.paymentDetails.bank.accountNumber;
            }
        },
        SetBusinessRegistrationNumberLabel(countryCode) {
            switch (countryCode) {
                case 'AU':
                    this.businessRegistrationNumberLabel = 'ABN';
                    break;
                case 'NZ':
                    this.businessRegistrationNumberLabel = 'NZBN';
                    break;
                default:
                    this.businessRegistrationNumberLabel = 'Business Registration Number';
                    break;
            }
        },
        /**
         *
         * @param {string | null} deviceType - either iOS or Android. may be null if the user hasn't logged in on the mobile app yet.
         * @returns {boolean}
         */
        canRequestDeviceLog(deviceType) {
            return (this.isSupportUser || this.isSupportAdminUser) && deviceType !== null;
        },
        /**
         *
         * @param {string} publicUserId
         * @returns {Promise<void>}
         */
        async handleRequestDeviceLog(publicUserId) {
            this.$_handleLoaderState(true, 'PROCESSING...');
            const payload = {
                method: 'post'
            };
            const api = `/api/support/device-log/${publicUserId}`;
            try {
                await handleRequests(api, payload);
                this.$_handleLoaderState(false);
                this.$notify({
                    message: 'Successfully sent request for device log',
                    type: 'success'
                });
            } catch (error) {
                this.$_handleLoaderState(false);
                const message = 'Error in getting request for device log';
                showErrorMessage(this, message, error);
            }
        },
        handleTabSwitch({ currentTab, newTab }) {
            let hasPendingChanges = false;
            switch (currentTab) {
                case 'Details':
                    hasPendingChanges = this.$refs.details.hasChanges();
                    break;
                case 'Documents':
                    hasPendingChanges = this.$refs.documents.hasChanges();
                    break;
                case 'Accounts':
                    hasPendingChanges = this.$refs.accounts.hasChanges();
                    break;
                case 'Rostering':
                    hasPendingChanges = this.$refs.rosterConfiguration.hasChanges();
                    break;
                default:
                    break;
            }

            if (hasPendingChanges) {
                this.showConfirmationMessageBox(newTab);
                return;
            }

            this.activeTab = newTab;
        },
        async showConfirmationMessageBox(panel) {
            let message = 'There are pending changes, are you sure you want to switch tabs?';
            if (this.messageText) {
                message = this.messageText;
            }
            let response = false;
            response = await this.$messageBox.show({
                class: 'sm-modal-container',
                title: 'Warning',
                body: `<p>${message}</p>`,
                buttons: ['Yes', 'Cancel']
            });

            if (response.toLowerCase() === 'yes') {
                this.activeTab = panel;
            }
        },
        async getRosterConfiguration() {
            this.$_handleLoaderState(true);
            try {
                const endpoint = `/api/team-members/roster-configuration/${this.$route.params.publicUserId}/`;
                const response = await handleRequests(endpoint);

                this.rosterConfigurationData = response?.data;
                this.rosterConfigurationData.blockoutDays.forEach((blockoutDay) => {
                    blockoutDay.isTouched = false;
                    blockoutDay.inEditMode = false;
                    blockoutDay.inAddMode = false;
                });

                if (this.rosterConfigurationData.fortnightlyStartDate) {
                    this.rosterConfigurationData.fortnightlyStartDate = moment(
                        this.rosterConfigurationData.fortnightlyStartDate
                    ).format(DATE_TYPES.internationalDate);
                }

                this.$_handleLoaderState(false);
            } catch (error) {
                const message = 'Error in loading roster configuration';
                showErrorMessage(this, message, error);
                this.$_handleLoaderState(false);
            }
        },
        async loadRateGroups() {
            try {
                const response = await handleRequests('/api/rate-groups');
                if (response?.data) {
                    this.rateGroups = [
                        { rateGroupId: -1, rateGroupName: 'Default' },
                        ...response.data.map((rg) => ({
                            rateGroupId: rg.rateGroupId,
                            rateGroupName: rg.rateGroupName
                        }))
                    ];
                }
            } catch (error) {
                console.error('Error loading rate groups:', error);
            }
        },
        async loadBusinessEntities() {
            try {
                const response = await handleRequests('/api/business-entities/list');
                if (response?.data?.businessEntities) {
                    this.businessEntities = response.data.businessEntities;
                }
            } catch (error) {
                console.error('Error loading business entities:', error);
            }
        },
        async loadAllTags() {
            try {
                const response = await handleRequests('/api/teams/settings/tags?showDeletedTags=true');
                if (response?.data) {
                    this.allTags = response.data;
                }
            } catch (error) {
                console.error('Error loading tags:', error);
            }
        },
        async getTeamMemberTags() {
            const api = `/api/team-members/tags/${this.member.userId}`;
            try {
                const response = await handleRequests(api);
                if (response?.data) 
                    this.member.tags = [...response.data];
            } catch (error) {
                const message = 'Error in getting tags';
                showErrorMessage(this, message, null);
            }
        },
        async getTeamMemberOnHoldDays() {
            const api = `/api/team-members/onHoldDays/${this.member.userId}`;
            try {
                const response = await handleRequests(api);
                if (response?.data) 
                    this.member.onHoldDays = response.data;
            } catch (error) {
                const message = 'Error in getting on hold dates';
                showErrorMessage(this, message, null);
            }
        },

        refreshHistory() {
            // Delay refresh to allow CDC to capture the changes before querying
            setTimeout(() => {
                if (this.$refs.historyComponent && typeof this.$refs.historyComponent.refresh === 'function') {
                    this.$refs.historyComponent.refresh();
                }
            }, 3000);
        },

        handleRosterConfigurationUpdated() {
            // Refresh roster configuration data
            this.getRosterConfiguration();
            // Refresh history to show rostering changes
            this.refreshHistory();
        }
    }
};
</script>

<style lang="scss" scoped>
::v-deep .custom-btn {
    margin-right: 10px;
}

.md-field.md-theme-default.md-invalid:after {
    background-color: red !important;
}

.edit-actions {
    margin-top: 20px;
    padding-right: 30px;
}

.vehicle-box {
    label {
        margin-right: 10px;
    }

    .md-radio {
        width: 65px;
    }
}

.profile-container {
    margin-top: 20px;
}

.custom-switch {
    margin-top: 22px;
    margin-bottom: 0px;

    label {
        float: left;
        font-weight: 400;
        color: #aaaaaa !important;
        line-height: 1.4;
    }

    .md-checked {
        ::v-deep .md-switch-container {
            background-color: #2b93ff !important;
        }

        ::v-deep .md-switch-thumb {
            border: 1px solid #2b93ff !important;
        }
    }

    ::v-deep .md-switch-label {
        margin-top: 0px;
    }

    .md-switch {
        margin: 0;
        margin-bottom: 10px;
        padding-left: 10px;
    }
}

.unlock-button {
    ::v-deep .md-button-content {
        color: #4caf50;
    }
}

.icon-info {
    color: #2b93ff !important;
    cursor: pointer;
    padding-bottom: 4px;
    margin-right: 15px;
}

.tooltip-width {
    max-width: 600px;
    text-align: justify;
}

.half-width {
    width: 50% !important;
}

.custom-tab-list {
    ::v-deep .tab-content {
        margin-top: 20px;
    }

    ::v-deep .nav-tabs {
        margin-left: -5px;
        background-color: transparent !important;
        border-bottom: none;
        // top: -60px;
    }

    ::v-deep .md-card-content {
        top: -60px;
    }

    ::v-deep .md-list-item-button {
        background-color: #fff;
    }
}

.custom-card-content {
    padding: 0 5px;
}

.card-container .md-button {
    width: 190px;
}

.history-section {
    margin-top: 20px;

    ::v-deep .md-card-content {
        padding: 0;
    }
}
</style>
